/**
 * The policy's remote-control ceiling as a form: which ways in are allowed, how much a paired
 * device may do, encrypted connections only, remote control kept off, the idle timeout. It reads
 * and writes the `remote` clause of the policy document the editor holds as JSON, so the form and
 * the document never disagree; each machine's Remote control card shows the limits that result.
 */

import { CONTROL_SCOPES, REMOTE_TRANSPORTS, remotePolicySchema, type ControlScope } from '@aioe/shared';

import { inputClass, Tick } from './ui.tsx';

type Transport = (typeof REMOTE_TRANSPORTS)[number];

const TRANSPORT_TEXT: Record<Transport, { label: string; hint: string }> = {
  ipc: { label: 'This machine only', hint: 'Apps on the same computer, such as the console in a browser there' },
  companion: { label: 'Companion apps', hint: 'MojoFlow and other apps that pair on the same computer' },
  lan: { label: 'The local network', hint: 'A phone or laptop on the same network, paired by code' },
  relay: { label: 'Through the organisation', hint: 'The console and the phone app, over the relay or the mesh' },
};

const SCOPE_TEXT: Record<ControlScope, string> = {
  'agents:read': 'See agents and their approvals',
  'agents:write': 'Start, stop and restart agents',
  'prompts:write': 'Send prompts and steer agents',
  'composer:write': 'Type into the composer',
  'permissions:write': 'Approve or refuse what agents ask',
  'teams:write': 'Start and stop teams',
  'tasks:read': 'Read the task board',
  'tasks:write': 'Change the task board',
  'memory:write': 'Write to memory',
  'memory:read': 'Read memory',
  'settings:write': 'Change settings',
  'git:write': 'Commit, push and merge',
  'ui:view': 'View the screen',
  'ui:control': 'Control the screen',
  'diagrams:read': 'See diagrams',
  'diagrams:write': 'Change diagrams',
  'terminals:read': 'Watch terminals',
  'terminals:write': 'Type into terminals',
  'home:read': 'See Home',
  'shell:read': "Watch a node's console",
  'shell:write': "Type in a node's console",
};

type Remote = ReturnType<typeof remotePolicySchema.parse>;

/** The clause as the document has it, or undefined when the document is not valid JSON. */
function read(json: string): { doc?: Record<string, unknown>; remote?: Remote } {
  try {
    const doc = JSON.parse(json) as Record<string, unknown>;
    const parsed = remotePolicySchema.safeParse(doc.remote ?? {});
    return { doc, remote: parsed.success ? parsed.data : remotePolicySchema.parse({}) };
  } catch {
    return {};
  }
}

export function RemotePolicyForm({ json, onChange }: { json: string; onChange: (json: string) => void }) {
  const { doc, remote } = read(json);
  if (!doc || !remote) return <p className="text-xs text-muted">The remote-control ceiling can be edited as a form once the document is valid JSON.</p>;
  const write = (next: Partial<Remote>) => onChange(JSON.stringify({ ...doc, remote: { ...remote, ...next } }, null, 2));
  const limited = remote.maxScopes !== undefined;
  const scopes = new Set(remote.maxScopes ?? CONTROL_SCOPES);
  const toggleTransport = (t: Transport, on: boolean) => write({ allowTransports: REMOTE_TRANSPORTS.filter((x) => (x === t ? on : remote.allowTransports.includes(x))) });
  const toggleScope = (s: ControlScope, on: boolean) => {
    const next = new Set(scopes);
    if (on) next.add(s);
    else next.delete(s);
    write({ maxScopes: CONTROL_SCOPES.filter((x) => next.has(x)) });
  };
  return (
    <fieldset className="grid gap-3 rounded-xl border border-separator p-3 text-sm">
      <legend className="px-1 text-xs text-muted">Remote control ceiling · the tightest limit across the organisation's and the repository's policies applies</legend>
      <div className="grid gap-1">
        <div className="text-xs text-muted">Ways in</div>
        {REMOTE_TRANSPORTS.map((t) => {
          // Companion apps are the same trust class as this machine: a list naming ipc allows them too.
          const implied = t === 'companion' && remote.allowTransports.includes('ipc');
          const on = remote.allowTransports.includes(t) || implied;
          return (
            <Tick key={t} checked={on} isDisabled={implied} onChange={(v) => toggleTransport(t, v)}>
              <span className={implied ? 'text-muted' : ''}>
                {TRANSPORT_TEXT[t].label}
                <span className="text-muted"> · {implied ? 'allowed with "this machine only"' : TRANSPORT_TEXT[t].hint}</span>
              </span>
            </Tick>
          );
        })}
      </div>
      <div className="grid gap-1">
        <Tick checked={limited} onChange={(v) => write({ maxScopes: v ? [...CONTROL_SCOPES] : undefined })}>
          <span>
            Limit what a paired device may do
            <span className="text-muted"> · {limited ? `${scopes.size} of ${CONTROL_SCOPES.length} allowed` : 'no limit: a pairing may carry any scope the person grants'}</span>
          </span>
        </Tick>
        {limited && (
          <div className="grid gap-1 pl-6 sm:grid-cols-2">
            {CONTROL_SCOPES.map((s) => (
              <Tick key={s} checked={scopes.has(s)} onChange={(v) => toggleScope(s, v)}>
                {SCOPE_TEXT[s]}
              </Tick>
            ))}
          </div>
        )}
      </div>
      <Tick checked={remote.requireTls === true} onChange={(v) => write({ requireTls: v ? true : undefined })}>
        <span>
          Encrypted connections only
          <span className="text-muted"> · the local-network listener refuses plain HTTP whatever the machine's own setting says</span>
        </span>
      </Tick>
      <Tick checked={remote.killSwitchLockedOn === true} onChange={(v) => write({ killSwitchLockedOn: v ? true : undefined })}>
        <span>
          Keep remote control switched off
          <span className="text-muted"> · the kill switch stays on and cannot be lifted while this policy applies</span>
        </span>
      </Tick>
      <label className="grid gap-1 text-xs text-muted sm:w-64">
        Idle timeout (minutes)
        <input type="number" min={1} max={1440} className={inputClass} value={remote.idleTimeoutMinutes} onChange={(e) => write({ idleTimeoutMinutes: Math.min(1440, Math.max(1, Number(e.target.value) || 60)) })} />
      </label>
    </fieldset>
  );
}
